'use client'

import { Divider, ListHeader, TextButton, Image } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useRef } from 'react'

import { ROUTES } from '@/shared/lib/routes'
import { OptimizedImage } from '@/shared/ui'

import ContentStats from './ContentStats'
import { carStoryTracking } from '../lib/tracking'
import { type IMycleContentCard } from '../model'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface IDriveAllSectionProps {
  items: IMycleContentCard[]
}

const DriveAllSection = ({ items }: IDriveAllSectionProps) => {
  const router = useRouter()

  if (items.length === 0) return null

  const handleMoreClick = () => {
    carStoryTracking.clickDriveAllMore()
    router.push(ROUTES.CAR_STORY.TAB('drive'))
  }

  return (
    <>
      <Divider.Horizontal strokeWidth='stroke-width-x10' />
      <section className='pt-dimension-x3 pb-dimension-x10 flex flex-col'>
        <ListHeader
          title={
            <ListHeader.Main.Title.Text
              text='드라이브의 모든 것'
              typography='heading-3-bold'
            />
          }
          right={
            <ListHeader.Main.Right.Accessory>
              <TextButton
                text='전체보기'
                variant='chevron'
                chevron='ic-chevron-right'
                typography='body-1-medium'
                textColor='foreground-neutral-alternative'
                iconColor='foreground-neutral-alternative'
                clickable={handleMoreClick}
              />
            </ListHeader.Main.Right.Accessory>
          }
        />
        <ul className='gap-y-dimension-x4 gap-x-dimension-x3 px-dimension-x5 grid grid-cols-2'>
          {items.map(item => (
            <li key={item.serverId}>
              <DriveAllCard item={item} />
            </li>
          ))}
        </ul>
      </section>
    </>
  )
}

const DriveAllCard = ({ item }: { item: IMycleContentCard }) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null)
  useTrackOnVisible({
    ref: cardRef,
    tracker: () => carStoryTracking.showDriveAllCard(item.title),
  })

  return (
    <Link
      ref={cardRef}
      href={ROUTES.CAR_STORY.CONTENT_DETAIL(item.serverId)}
      onClick={() => carStoryTracking.clickDriveAllCard(item.title)}
      className='gap-y-dimension-x2-5 flex flex-col'
    >
      <div className='bg-background-neutral-weak rounded-dimension-x3 relative aspect-square w-full overflow-hidden'>
        <OptimizedImage
          src={item.thumbnailUrl}
          alt={item.title}
          width='100%'
          height='100%'
          radius={0}
          scaletype='crop'
          sizes='(max-width: 480px) 50vw, 240px'
          fallback={<Image.Fallback />}
        />
      </div>
      <div className='gap-y-dimension-x1-5 flex flex-col'>
        <p className='text-title-2-medium text-foreground-neutral-strong line-clamp-2 h-dimension-x11 whitespace-pre-line'>
          {item.title}
        </p>
        {item.stat && <ContentStats stat={item.stat} />}
      </div>
    </Link>
  )
}

export default DriveAllSection
